import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ClientTherapistRelationship } from "@/types/therapist";
import { Badge } from "@/components/ui/badge";
import { Calendar, MapPin, Globe, FileText, UserCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface ClientProfileModalProps {
  clientRelationship: ClientTherapistRelationship;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  getClientDisplayName: (client?: ClientTherapistRelationship['client']) => string;
  formatDate: (dateString: string) => string;
}

interface UserProfile {
  user_id?: string;
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
  show_activity?: boolean;
  country?: string | null;
  language?: string | null;
  language_secondary?: string | null;
  profile_bio?: string | null;
}

const ClientProfileModal = ({
  clientRelationship,
  open,
  onOpenChange,
  getClientDisplayName,
  formatDate
}: ClientProfileModalProps) => {
  const [isOpen, setIsOpen] = useState(open);
  const [clientProfile, setClientProfile] = useState<UserProfile>({});
  const clientId = clientRelationship?.client_id;

  // Sync internal state with props
  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  // Handle internal state changes
  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    onOpenChange(newOpen);
  };

  useEffect(() => {
    if (!clientId) return;

    const fetchClientProfile = async () => {
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', clientId)
          .maybeSingle();

        if (error) {
          console.error("Error fetching client profile:", error);
        } else if (data) {
          console.log("Client profile data:", data);
          setClientProfile(data);
        }
      } catch (error) {
        console.error("Error in profile fetch:", error);
      }
    };

    fetchClientProfile();
  }, [clientId]);

  if (!clientRelationship) return null;

  const clientName = getClientDisplayName(clientRelationship.client);
  const relationshipDate = formatDate(clientRelationship.created_at);
  const disconnectDate = clientRelationship.disconnected_at ? formatDate(clientRelationship.disconnected_at) : null;
  const isSharing = clientProfile?.show_activity === true;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" className="bg-white hover:bg-[#E8F0F8]">
          <UserCircle2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl">Client Profile</DialogTitle>
          <DialogDescription>
            Details for {clientName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="border rounded-lg p-4 bg-muted/30">
            <h3 className="font-medium mb-2">Relationship Information</h3>
            <div className="flex gap-2 items-center mb-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Connected since {relationshipDate}</span>
            </div>
            {clientRelationship.status === 'inactive' && disconnectDate && (
              <div className="flex gap-2 items-center mb-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Disconnected since {disconnectDate}</span>
              </div>
            )}
            <Badge variant="outline" className={isSharing ? 
              "grayscale-0 opacity-100 text-green-700 bg-green-50 border-green-200" : 
              "bg-amber-50 text-amber-700 hover:bg-amber-50 border-amber-200"}>
              {isSharing ? "SHARED" : "NOT SHARED"}
            </Badge>
          </div>

          <Card>
            <CardContent className="pt-6">
              <h3 className="font-medium mb-3">Location & Language</h3>
              {clientProfile?.country && (
                <div className="flex gap-2 items-center mb-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    <span className="font-medium">Country:</span> {clientProfile.country}
                  </span>
                </div>
              )}
              {clientProfile?.language && (
                <div className="flex gap-2 items-center mb-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    <span className="font-medium">Languages:</span> {clientProfile.language}
                    {clientProfile.language_secondary && `, ${clientProfile.language_secondary}`}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {clientProfile?.profile_bio && (
            <Card>
              <CardContent className="pt-6">
                <div className="flex gap-2 items-start mb-2">
                  <FileText className="h-4 w-4 text-muted-foreground mt-1" />
                  <div>
                    <h3 className="font-medium mb-1">Bio</h3>
                    <p className="text-sm text-muted-foreground whitespace-pre-line">{clientProfile.profile_bio}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClientProfileModal;
